/**
 * Index page router — chooses what to show at / based on app settings.
 * - Maintenance mode: show maintenance page
 * - Live production OFF: show Coming Soon (admins bypass)
 * - Authenticated users: redirect to home/onboarding
 * - Logged-out users: show EarlyAccessLanding
 */

import { Link, Navigate } from 'react-router';
import { useAppSettings } from '../lib/app-settings-context';
import { useAdminRole } from '../lib/use-admin-role';
import { useAuth } from '../lib/auth-context';
import { useAccountBootstrapReady } from '../lib/use-account-bootstrap-ready';
import EarlyAccessLanding from './EarlyAccessLanding';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Wrench, Sparkles } from 'lucide-react';
import { isOnboardingComplete } from '../lib/onboarding-state';

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function MaintenancePage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-900 p-4">
      <Card className="max-w-md w-full p-8 text-center bg-neutral-800 border-red-700">
        <div className="bg-red-900/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-700">
          <Wrench className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">Down for Maintenance</h1>
        <p className="text-neutral-300 mb-4">
          TENSION is currently undergoing scheduled maintenance. We&apos;ll be back soon!
        </p>
        <p className="text-sm text-neutral-500">Thank you for your patience.</p>
      </Card>
    </div>
  );
}

function ComingSoonPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-neutral-900 to-neutral-950 p-4">
      <Card className="max-w-md w-full p-8 text-center bg-neutral-800/80 border-violet-700/50">
        <div className="bg-violet-900/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-violet-500/50">
          <Sparkles className="w-10 h-10 text-violet-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">Coming Soon</h1>
        <p className="text-neutral-300 mb-4">
          TENSION is getting ready. We&apos;re putting the finishing touches on something new for your relationships.
        </p>
        <p className="text-sm text-neutral-500 mb-4">Check back soon.</p>
        <Link to="/auth">
          <Button variant="ghost" size="sm" className="text-neutral-400 hover:text-white">
            Staff? Sign in
          </Button>
        </Link>
      </Card>
    </div>
  );
}

export default function IndexPageRouter() {
  const { maintenanceMode, liveProduction } = useAppSettings();
  const { isAdmin } = useAdminRole();
  const { user, loading } = useAuth();
  const accountBootstrapReady = useAccountBootstrapReady();

  if (maintenanceMode) {
    return <MaintenancePage />;
  }

  if (!liveProduction && !isAdmin) {
    return <ComingSoonPage />;
  }

  // Signed-in users go to app, not splash/landing (wait for cloud merge so localStorage matches account)
  if (!loading && user && !accountBootstrapReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-900">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-violet-500 border-t-transparent"
          aria-hidden
        />
      </div>
    );
  }

  if (!loading && user) {
    const mode = safeLocalStorageGet('tension_mode');
    const hasCompletedOnboarding = isOnboardingComplete();
    if (hasCompletedOnboarding && mode) {
      return <Navigate to="/home" replace />;
    }
    if (mode) {
      return <Navigate to={mode === 'single' ? '/singles-onboarding' : '/onboarding'} replace />;
    }
    return <Navigate to="/mode-selection" replace />;
  }

  return <EarlyAccessLanding />;
}
